"use client";

import { ChartSpline } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function OddsHistoryTrigger({
  mode,
  onOpen,
  bookLabel,
  label,
  className,
}: {
  mode: "reveal" | "inline";
  onOpen: () => void;
  bookLabel: string;
  label?: string;
  className?: string;
}) {
  const isReveal = mode === "reveal";
  const button = (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      aria-label={`Show line history for ${bookLabel}`}
      className={cn(
        "inline-flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        isReveal
          ? cn(
              "size-6 ring-1 ring-white/[0.08] bg-surface-raised/90 backdrop-blur-sm text-muted-foreground/80 transition-[opacity,color,background-color,box-shadow] hover:text-foreground hover:bg-white/[0.06] hover:ring-white/[0.15]",
              "pointer-events-auto",
              "[@media(hover:hover)]:pointer-events-none [@media(hover:hover)]:opacity-0",
              "[@media(hover:hover)]:group-hover/history:pointer-events-auto [@media(hover:hover)]:group-hover/history:opacity-100",
              "[@media(hover:hover)]:group-focus-within/history:pointer-events-auto [@media(hover:hover)]:group-focus-within/history:opacity-100",
              "[@media(hover:hover)]:focus-visible:pointer-events-auto [@media(hover:hover)]:focus-visible:opacity-100",
            )
          : cn(
              "gap-1 min-h-8 min-w-8 border border-white/[0.08] bg-white/[0.04] px-2 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-white/[0.08] hover:border-white/[0.15] hover:text-foreground",
              "pointer-events-auto",
            ),
        className,
      )}
    >
      <ChartSpline aria-hidden className="size-3.5" />
      {label ? <span>{label}</span> : null}
    </button>
  );

  if (label) return button;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          Line history
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
